import OpenAI from 'openai';
import { Settings } from '../types';
import { ChatCompletionResponse, OpenAIError } from '../types/openai';

let openai: OpenAI | null = null;

export function initializeOpenAI(settings: Settings) {
  openai = new OpenAI({
    baseURL: settings.openaiUrl,
    apiKey: settings.openaiKey,
    dangerouslyAllowBrowser: true,
  });
}

export async function imageToText(imageBase64: string, settings: Settings): Promise<string> {
  if (!openai) {
    initializeOpenAI(settings);
  }

  try {
    const response = await openai!.chat.completions.create({
      model: settings.imageToTextModel,
      messages: [
        {
          role: 'system',
          content: "You job is to transform the words you see in an image into a detailed description that" + 
          " will be given to a professional artist to create an image from. Do not describe any images, objects, or page numbers (if it's a book). " + 
          "Do not describe the layout of the page. Do not describe the paragraph number it appeared in. " + 
          "Provide the description in the form of a bulleted list of items. Do not mention what you are doing. Just provide the detailed descriptions.",
        },
        {
          role: 'user',
          content: [
            //{ type: 'text', text: 'Please provide a detailed description of what you see in the image.' },
            {
              type: 'image_url',
              image_url: {
                url: imageBase64,
              },
            },
          ],
        },
      ],
    }) as ChatCompletionResponse;

    return response.choices[0].message.content || '';
  } catch (error) {
    console.error('Error in imageToText:', error);
    if (error instanceof Error) {
      alert(`Failed to process image: ${error.message}`)
      throw new Error(`Failed to process image: ${error.message}`);
    } else {
      alert(`Failed to process image: Unknown error occurred`)
      throw new Error('Failed to process image: Unknown error occurred');
    }
  }
}

export async function generateDescription(text: string, settings: Settings): Promise<string> {
  if (!openai) {
    initializeOpenAI(settings);
  }

  try {
    const response = await openai!.chat.completions.create({
      model: settings.textModel,
      messages: [
        {
          role: 'system',
          content: "You have been provided a random chunk of text that an image has been created from. Please provide a very short description of what the image is about. It should be no more than 10 words.",
        },
        {
          role: 'user',
          content: `Below is the text you need to describe.\n\n${text}`,
        },
      ],
    }) as ChatCompletionResponse;

    return response.choices[0].message.content || '';
  } catch (error) {
    const openaiError = error as OpenAIError;
    console.error('Error in generateDescription:', openaiError.error.message);
    alert(`Error in generateDescription: ${openaiError.error.message}`);
    throw error;
  }
}

export async function generateImage(prompt: string, settings: Settings): Promise<string> {
  try {
    console.log('Generating image with prompt:', prompt, settings.imageRouterKey);
    // const url = 'https://ir-api.myqa.cc/v1/openai/images/generations';
    const url = 'https://api.imagerouter.io/v1/openai/images/generations';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${settings.imageRouterKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt,
        model: settings.textToImageModel,
        response_format: 'b64_json'
      })
    });

    if (!response.ok) {
      let responseText = "Unknown error";
      try {
        responseText = await response.text();
      } catch {}
      throw new Error(`HTTP error! status: ${response.status}, ${responseText}`);
    }

    const data = await response.json();
    if (!data.data?.[0]?.b64_json) {
      throw new Error('No base64 image data received from the API');
    }
    return `data:image/png;base64,${data.data[0].b64_json}`;
  } catch (error: unknown) {
    console.error('Error in generateImage:', error);
    alert(`Error in generateImage: ${error?.toString()}`);
    throw error;
  }
} 